import React, { FunctionComponent, useEffect, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Color } from '../../config/Color';
import { greenTick } from '../assets/images/Icons';
import { BodySM } from '../styles/Typography';

// Animation keyframes
const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideDown = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(20px);
  }
`;

// Toast container
const ToastContainer = styled.div<{ isVisible: boolean; isExiting: boolean }>`
  position: fixed;
  bottom: 80px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;

  ${({ isVisible, isExiting }) => {
    if (!isVisible)
      return css`
        display: none;
      `;
    if (isExiting) {
      return css`
        animation: ${slideDown} 0.3s ease-in forwards;
      `;
    }
    return css`
      animation: ${slideUp} 0.3s ease-out forwards;
    `;
  }}
`;

const ToastContent = styled.div`
  display: flex;
  align-items: center;
  background-color: #000000;
  border: 1px solid ${Color.decorative.muted};
  border-radius: 8px;
  padding: 12px 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-width: 300px;
  margin: 0 20px; // Ensure margin from screen edges
`;

const CheckIcon = styled.img`
  width: 20px;
  height: 20px;
  margin-right: 8px;
  flex-shrink: 0;
`;

const ToastText = styled(BodySM)`
  color: ${Color.content.default};
  margin: 0;
`;

export type ToastNotificationProps = {
  /** The text to display in the toast */
  message: string;
  /** Whether the toast is currently visible */
  isVisible: boolean;
  /** Duration in milliseconds before the toast starts to hide */
  duration: number;
  /** Callback when toast completes its hide animation */
  onComplete?: () => void;
};

export const ToastNotification: FunctionComponent<ToastNotificationProps> = ({
  message,
  isVisible,
  duration,
  onComplete,
}) => {
  const [isExiting, setIsExiting] = useState(false);

  useEffect(
    function handleToastVisibility() {
      if (!isVisible) {
        setIsExiting(false);
        return;
      }

      // Start exit animation after duration
      const hideTimer = setTimeout(() => {
        setIsExiting(true);
      }, duration);

      // Call onComplete after exit animation completes
      const completeTimer = setTimeout(() => {
        onComplete?.();
      }, duration + 300); // 300ms for exit animation

      return () => {
        clearTimeout(hideTimer);
        clearTimeout(completeTimer);
      };
    },
    [isVisible, duration, onComplete]
  );

  if (!isVisible && !isExiting) {
    return null;
  }

  return (
    <ToastContainer isVisible={isVisible} isExiting={isExiting}>
      <ToastContent>
        <CheckIcon src={greenTick} alt="Success" />
        <ToastText>{message}</ToastText>
      </ToastContent>
    </ToastContainer>
  );
};
